/* SUMMARY
  * Imports
  * Interfaces
  * DateRangeSelect: date range picker with time using datetime-local inputs
    * useEffect: update value from prop
    * useEffect: auto-calculate duration on datetime change
    * useEffect: notify parent of changes (debounced)
    * calculateDuration: calculate duration in minutes
    * handleChange: change event handler
    * handleInputClick: open datetime picker on input click
*/

/* Imports */
import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { t } from "../../../i18n";
/***/

/* Interfaces */
interface IRange {
  start: string,
  end: string | null,
  duration?: number | string | null,
  [key: string]: string | number | null | undefined
}
/***/

/**
* DateRangeSelect: date range picker with datetime-local inputs
* @param range - current range value
* @param onChange - on range change event
* @param hideDuration - hide duration field
* @param className - additional classname
* @param onErrorClass - on error class
* @param disabled - disable date and duration inputs
* @param disallowFuture - cap both datetimes at the current time
*/
export default function DateRangeSelect({
  range,
  onChange,
  hideDuration,
  className = "",
  onErrorClass = "",
  disabled = false,
  disableAutoDuration = false,
  disallowFuture = false
}: {
  range?: IRange,
  onChange?: (data: IRange) => void,
  hideDuration?: boolean,
  className?: string,
  onErrorClass?: string,
  disabled?: boolean,
  disableAutoDuration?: boolean,
  disallowFuture?: boolean
}) {
  const [startDateTime, setStartDateTime] = useState<string>("");
  const [endDateTime, setEndDateTime] = useState<string>("");
  const [duration, setDuration] = useState<string>("0");
  const [durationEditedManually, setDurationEditedManually] = useState(false);
  const [durationFromParent, setDurationFromParent] = useState(false);
  const [isInvalidDateRange, setIsInvalidDateRange] = useState(false);
  
  const startInputRef = useRef<HTMLInputElement>(null);
  const endInputRef = useRef<HTMLInputElement>(null);
  const lastNotifiedValueRef = useRef<string>("");
  const onChangeRef = useRef(onChange);
  const durationInitializedRef = useRef(false);
  // The inputs start empty, then a later effect copies `range` into them. A clear notification
  // before the user has typed would wipe a schedule that has not been painted yet.
  const userEditedRef = useRef(false);

  /**
  * calculateDuration: calculate duration in minutes between two datetime-local values
  * Format: YYYY-MM-DDTHH:mm:ss
  * Returns duration and sets error flag if end is before start
  */
  const calculateDuration = (start: string, end: string): string => {
    if (!start || !end) {
      setIsInvalidDateRange(false);
      return "";
    }
    
    try {
      const startDateObj = new Date(start);
      const endDateObj = new Date(end);

      if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
        setIsInvalidDateRange(false);
        return "0";
      }

      const diffMs = endDateObj.getTime() - startDateObj.getTime();
      const minutes = Math.round(diffMs / 60000);
      
      // Check if end is before start
      if (minutes < 0) {
        setIsInvalidDateRange(true);
        return "0";
      }
      
      setIsInvalidDateRange(false);
      return minutes.toString();
    } catch (error) {
      setIsInvalidDateRange(false);
      return "0";
    }
  };
  /***/

  /**
  * useEffect: update value from range prop
  * Load start/end dates always, load duration only on first initialization
  */
  useEffect(() => {
    if (!range) return;

    try {
      let newStartDateTime = "";
      let newEndDateTime = "";

      if (range.start) {
        const startDate = new Date(range.start);
        newStartDateTime = format(startDate, "yyyy-MM-dd'T'HH:mm:ss");
      }

      if (range.end) {
        const endDate = new Date(range.end);
        newEndDateTime = format(endDate, "yyyy-MM-dd'T'HH:mm:ss");
      }

      setStartDateTime(newStartDateTime);
      setEndDateTime(newEndDateTime);
      setDurationEditedManually(false);

      // Load duration from parent only once on first initialization
      if (!durationInitializedRef.current && range.duration && range.duration !== "0") {
        setDuration(String(range.duration));
        setDurationFromParent(true);
        durationInitializedRef.current = true;
      } else if (!newEndDateTime && !range.duration) {
        setDuration("0");
        setDurationFromParent(false);
      }
    } catch (error) {
      return;
    }
  }, [range?.start, range?.end, range?.duration]);
  /***/

  /**
  * useEffect: auto-calculate duration when user changes datetimes
  * Only if duration wasn't loaded from parent and wasn't manually edited
  */
  useEffect(() => {
    if (!disableAutoDuration && startDateTime && endDateTime && !durationEditedManually && !durationFromParent) {
      const calculatedDuration = calculateDuration(startDateTime, endDateTime);
      setDuration(calculatedDuration);
    }
  }, [startDateTime, endDateTime, durationEditedManually, durationFromParent, disableAutoDuration]);
  /***/

  /**
  * useEffect: keep onChange ref updated
  */
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  /***/

  /**
  * useEffect: notify parent of changes (with deduplication)
  * Don't include onChange in dependencies to avoid infinite loops
  *
  * An empty start used to return early, so emptying the fields left the parent holding the
  * previous range and the save wrote those dates back.
  */
  useEffect(() => {
    if (!onChangeRef.current) {
      return;
    }

    if (!startDateTime) {
      if (!userEditedRef.current) {
        return;
      }

      let clearedEnd: string | null = null;
      if (endDateTime) {
        const endDate = new Date(endDateTime);
        if (isNaN(endDate.getTime())) {
          return;
        }
        clearedEnd = endDate.toISOString();
      }

      const cleared: IRange = { start: "", end: clearedEnd, duration: null };
      const clearedString = JSON.stringify(cleared);
      if (clearedString !== lastNotifiedValueRef.current) {
        lastNotifiedValueRef.current = clearedString;
        onChangeRef.current(cleared);
      }
      return;
    }

    try {
      const startDate = new Date(startDateTime);
      if (isNaN(startDate.getTime())) {
        return;
      }

      let endValue: string | null = null;
      if (endDateTime) {
        const endDate = new Date(endDateTime);
        if (isNaN(endDate.getTime())) {
          return;
        }
        endValue = endDate.toISOString();
      }

      const result: IRange = {
        start: startDate.toISOString(),
        end: endValue,
        duration: endValue ? Number(duration) : null
      };
      
      const resultString = JSON.stringify(result);
      if (resultString !== lastNotifiedValueRef.current) {
        lastNotifiedValueRef.current = resultString;
        onChangeRef.current(result);
      }
    } catch (error) {
      return;
    }
  }, [startDateTime, endDateTime, duration]);
  /***/

  /**
  * handleInputClick: open datetime picker on input click
  */
  const handleInputClick = (inputRef: React.RefObject<HTMLInputElement>) => {
    if (inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.showPicker) {
        inputRef.current.showPicker();
      }
    }
  };
  /***/

  /**
  * handleChange: change event handler
  * Auto-populate end datetime with +1 hour when start is selected
  */
  const handleChange = (key: string, value: string) => {
    userEditedRef.current = true;
    if (key === "startDateTime") {
      setStartDateTime(value);
      setDurationEditedManually(false);
      setDurationFromParent(false);
      if (!value) {
        setDuration("");
      }
      
      // Auto-populate endDateTime if empty
      if (!endDateTime) {
        try {
          const startDate = new Date(value);
          const suggested = new Date(startDate);
          suggested.setHours(suggested.getHours() + 1);

          // A range that may not run into the future gets its +1h suggestion capped at now, and is
          // left for the user to fill when even that would not land after the start.
          if (disallowFuture && suggested.getTime() > Date.now()) {
            const now = new Date();
            if (now.getTime() <= startDate.getTime()) return;
            setEndDateTime(format(now, "yyyy-MM-dd'T'HH:mm:ss"));
            return;
          }

          setEndDateTime(format(suggested, "yyyy-MM-dd'T'HH:mm:ss"));
        } catch (error) {
          return;
        }
      }
    } else if (key === "endDateTime") {
      setEndDateTime(value);
      setDurationEditedManually(false);
      setDurationFromParent(false);
      if (!value) {
        setDuration("");
      }
    } else if (key === "duration") {
      setDuration(value);
      setDurationEditedManually(true);
    }
  };
  /***/

  // Both bounds are datetime-local strings, so the earlier one is simply the smaller string.
  const nowBound = disallowFuture ? format(new Date(), "yyyy-MM-dd'T'HH:mm") : "";
  const endBound = endDateTime ? endDateTime.slice(0, 16) : "";
  const startMax = [endBound, nowBound].filter(Boolean).sort()[0] || "";

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className="field p-0 cursor-pointer" onClick={() => handleInputClick(startInputRef)}>
        <label>{t("common:startDate")} *</label>
        <input
          ref={startInputRef}
          type="datetime-local"
          className={`form-field ${onErrorClass}`}
          value={startDateTime ? startDateTime.slice(0, 16) : ""}
          max={startMax}
          disabled={disabled}
          onChange={(e) => handleChange("startDateTime", e.target.value)}
        />
      </div>

      <div className="field p-0 cursor-pointer" onClick={() => handleInputClick(endInputRef)}>
        <label>{t("common:endDate")} *</label>
        <input
          ref={endInputRef}
          type="datetime-local"
          className={`form-field ${onErrorClass}`}
          value={endDateTime ? endDateTime.slice(0, 16) : ""}
          min={startDateTime ? startDateTime.slice(0, 16) : ""}
          max={nowBound}
          disabled={disabled}
          onChange={(e) => handleChange("endDateTime", e.target.value)}
        />
      </div>

      {!hideDuration && (
        <div className="field p-0">
          <label>{t("common:durationMin")}</label>
          <input
            type="number"
            className={`form-field ${onErrorClass}`}
            value={duration}
            disabled={disabled}
            onChange={(e) => handleChange("duration", e.target.value)}
          />
        </div>
      )}
    </div>
  );
}
/***/
